import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Globe, Shield, Book, Users, Database } from "lucide-react";

// Create accordion components for the content sections
const AccordionItem = ({
  title,
  children,
  icon,
  isOpen,
  toggleOpen,
}: {
  title: string;
  children: React.ReactNode;
  icon: React.ReactNode;
  isOpen: boolean;
  toggleOpen: () => void;
}) => {
  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
      <button
        className={`w-full flex items-center justify-between p-5 text-left ${isOpen ? "bg-frikopplad-cream" : "bg-white"}`}
        onClick={toggleOpen}
      >
        <div className="flex items-center">
          <div className="mr-3 text-frikopplad-burgundy">{icon}</div>
          <h3 className="text-xl font-bold text-frikopplad-navy">{title}</h3>
        </div>
        <span className="text-2xl">{isOpen ? "−" : "+"}</span>
      </button>

      {isOpen && <div className="p-5 bg-white border-t border-gray-200">{children}</div>}
    </div>
  );
};

const Resources = () => {
  const { t } = useTranslation("resources");
  const [openSection, setOpenSection] = useState<string | null>("government");

  const toggleSection = (section: string) => {
    if (openSection === section) {
      setOpenSection(null);
    } else {
      setOpenSection(section);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-frikopplad-cream to-white">
          <div className="container-custom py-16">
            <h1 className="text-4xl md:text-5xl font-bold text-frikopplad-navy mb-6">{t("hero.title")}</h1>
            <p className="text-xl text-frikopplad-navy opacity-90 max-w-4xl">{t("hero.description")}</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="container-custom py-12">
          <div className="mx-auto max-w-4xl">
            <AccordionItem
              title={t("sections.government.title")}
              icon={<Globe className="h-6 w-6" />}
              isOpen={openSection === "government"}
              toggleOpen={() => toggleSection("government")}
            >
              <div className="prose max-w-none">
                <ul className="list-disc pl-5 space-y-4">
                  <li>
                    <p>
                      <strong>{t("sections.government.items.digg.title")}</strong> –{" "}
                      {t("sections.government.items.digg.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://www.digg.se"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.government.items.digg.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.government.items.osor.title")}</strong> –{" "}
                      {t("sections.government.items.osor.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://joinup.ec.europa.eu/collection/open-source-observatory-osor"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.government.items.osor.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.government.items.gaia.title")}</strong> –{" "}
                      {t("sections.government.items.gaia.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://gaia-x.eu"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.government.items.gaia.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.government.items.sovereign.title")}</strong> –{" "}
                      {t("sections.government.items.sovereign.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://sovereigntechfund.de"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.government.items.sovereign.link")}
                      </a>
                    </p>
                  </li>
                </ul>
              </div>
            </AccordionItem>

            <AccordionItem
              title={t("sections.projects.title")}
              icon={<Database className="h-6 w-6" />}
              isOpen={openSection === "projects"}
              toggleOpen={() => toggleSection("projects")}
            >
              <div className="prose max-w-none">
                <ul className="list-disc pl-5 space-y-4">
                  <li>
                    <p>
                      <strong>{t("sections.projects.items.catalog.title")}</strong> –{" "}
                      {t("sections.projects.items.catalog.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://interoperable-europe.ec.europa.eu/eu-oss-catalogue"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.projects.items.catalog.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.projects.items.ngizero.title")}</strong> –{" "}
                      {t("sections.projects.items.ngizero.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://nlnet.nl/NGI0"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.projects.items.ngizero.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.projects.items.openplatform.title")}</strong> –{" "}
                      {t("sections.projects.items.openplatform.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://www.oeforening.se/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.projects.items.openplatform.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.projects.items.alternatives.title")}</strong> –{" "}
                      {t("sections.projects.items.alternatives.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://european-alternatives.eu"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.projects.items.alternatives.link")}
                      </a>
                    </p>
                  </li>
                </ul>
              </div>
            </AccordionItem>

            <AccordionItem
              title={t("sections.orgs.title")}
              icon={<Shield className="h-6 w-6" />}
              isOpen={openSection === "orgs"}
              toggleOpen={() => toggleSection("orgs")}
            >
              <div className="prose max-w-none">
                <ul className="list-disc pl-5 space-y-4">
                  <li>
                    <p>
                      <strong>{t("sections.orgs.items.fsfe.title")}</strong> –{" "}
                      {t("sections.orgs.items.fsfe.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://fsfe.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.orgs.items.fsfe.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.orgs.items.ofe.title")}</strong> – {t("sections.orgs.items.ofe.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://openforumeurope.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.orgs.items.ofe.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.orgs.items.edri.title")}</strong> –{" "}
                      {t("sections.orgs.items.edri.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://edri.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.orgs.items.edri.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.orgs.items.opensource.title")}</strong> –{" "}
                      {t("sections.orgs.items.opensource.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://opensourcesweden.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.orgs.items.opensource.link")}
                      </a>
                    </p>
                  </li>
                </ul>
              </div>
            </AccordionItem>

            <AccordionItem
              title={t("sections.platforms.title")}
              icon={<Users className="h-6 w-6" />}
              isOpen={openSection === "platforms"}
              toggleOpen={() => toggleSection("platforms")}
            >
              <div className="prose max-w-none">
                <ul className="list-disc pl-5 space-y-4">
                  <li>
                    <p>
                      <strong>{t("sections.platforms.items.codeberg.title")}</strong> –{" "}
                      {t("sections.platforms.items.codeberg.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://codeberg.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.platforms.items.codeberg.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.platforms.items.mastodon.title")}</strong> –{" "}
                      {t("sections.platforms.items.mastodon.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://joinmastodon.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.platforms.items.mastodon.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.platforms.items.framasoft.title")}</strong> –{" "}
                      {t("sections.platforms.items.framasoft.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://framasoft.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.platforms.items.framasoft.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.platforms.items.dataportal.title")}</strong> –{" "}
                      {t("sections.platforms.items.dataportal.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://www.dataportal.se"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.platforms.items.dataportal.link")}
                      </a>
                    </p>
                  </li>
                </ul>
              </div>
            </AccordionItem>

            <AccordionItem
              title={t("sections.education.title")}
              icon={<Book className="h-6 w-6" />}
              isOpen={openSection === "education"}
              toggleOpen={() => toggleSection("education")}
            >
              <div className="prose max-w-none">
                <h4 className="font-bold mb-3">{t("sections.education.policy.title")}</h4>
                <p className="mb-4">{t("sections.education.policy.description")}</p>

                <h4 className="font-bold mb-3">{t("sections.education.studies.title")}</h4>
                <p className="mb-2">{t("sections.education.studies.description")}</p>
                <ul className="list-disc pl-5 space-y-4">
                  <li>
                    <p>
                      <strong>{t("sections.education.studies.items.impact.title")}</strong> –{" "}
                      {t("sections.education.studies.items.impact.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://digital-strategy.ec.europa.eu/en/library/study-about-impact-open-source-software-and-hardware-technological-independence-competitiveness-and"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.education.studies.items.impact.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.education.studies.items.joinup.title")}</strong> –{" "}
                      {t("sections.education.studies.items.joinup.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://interoperable-europe.ec.europa.eu/collection/open-source-observatory-osor"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.education.studies.items.joinup.link")}
                      </a>
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>{t("sections.education.studies.items.academic.title")}</strong> –{" "}
                      {t("sections.education.studies.items.academic.description")}
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://fosdem.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {t("sections.education.studies.items.academic.link")}
                      </a>
                    </p>
                  </li>
                </ul>
              </div>
            </AccordionItem>
          </div>

          {/* Call to Action */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="bg-frikopplad-cream p-8 rounded-xl shadow-sm">
              <h2 className="text-2xl font-bold text-frikopplad-navy mb-4">{t("cta.title")}</h2>
              <p className="text-lg text-frikopplad-navy/90 mb-6">{t("cta.description")}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="bg-frikopplad-blue hover:bg-frikopplad-navy" asChild>
                  <Link to="/paket">{t("cta.buttons.packages")}</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/varfor">{t("cta.buttons.why")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Resources;
